import { Controller } from '@hotwired/stimulus';
import { paint } from '../lib/dom';
import { ancestors, basename, directories, inside, moved, parentOf } from '../lib/paths';

export default class extends Controller {
  static targets = ['aside', 'fileTemplate', 'folderTemplate', 'markdownTemplate', 'nameTemplate', 'root', 'tree'];

  static values = { baseIndent: Number, indentStep: Number, nameErrors: Object, placeholders: Object };

  static classes = ['dragged', 'dropTarget', 'entryActive', 'entryIdle', 'iconActive', 'iconIdle'];

  initialize() {
    this.scene = { files: [], emptyFolders: [], activePath: null };
    this.collapsed = new Set();
    this.editing = null;
    this.nameRow = null;
    this.dragged = null;
    this.dragRow = null;
    this.dropRow = null;
  }

  render(scene) {
    this.scene = scene;

    this.#renderTree();
  }

  openFile(event) {
    this.dispatch('open', { detail: { path: event.currentTarget.dataset.path } });
  }

  toggleFolder(event) {
    this.#toggleFolder(this.#folderOf(event));
  }

  newFile() {
    this.#edit({ mode: 'create', type: 'file', parent: '' });
  }

  newFolder() {
    this.#edit({ mode: 'create', type: 'folder', parent: '' });
  }

  newFileInFolder(event) {
    this.#edit({ mode: 'create', type: 'file', parent: this.#folderOf(event) });
  }

  newFolderInFolder(event) {
    this.#edit({ mode: 'create', type: 'folder', parent: this.#folderOf(event) });
  }

  renameFile(event) {
    this.#startRename(event.currentTarget.closest('[data-path]').dataset.path, 'file');
  }

  renameFolder(event) {
    this.#startRename(this.#folderOf(event), 'folder');
  }

  renameEntry(event) {
    const entry = this.#entryFrom(event);

    if (entry === null) return;
    if (entry.dataset.folder !== undefined) return this.#startRename(entry.dataset.folder, 'folder');

    this.#startRename(entry.dataset.path, 'file');
  }

  deleteFile(event) {
    const path = event.currentTarget.closest('[data-path]').dataset.path;

    this.dispatch('remove', { detail: { path, type: 'file' } });
  }

  deleteFolder(event) {
    this.dispatch('remove', { detail: { path: this.#folderOf(event), type: 'folder' } });
  }

  validateName(event) {
    this.#showNameError(this.#nameError(event.target.value));
  }

  confirmName(event) {
    if (this.#nameError(event.target.value) !== null) return;
    if (this.#cleaned(event.target.value) === '') return this.cancelName();

    event.target.blur();
  }

  cancelName() {
    this.editing = null;
    this.nameRow = null;

    this.#renderTree();
  }

  commitName(event) {
    const context = this.editing;
    const rejected = context !== null && this.#nameError(event.target.value) !== null;
    const name = this.#cleaned(event.target.value);

    this.editing = null;
    this.nameRow = null;

    if (context === null) return;
    if (rejected || name === '') return this.#renderTree();

    const path = context.parent ? `${context.parent}/${name}` : name;

    if (context.mode === 'rename') return this.#rename(context.path, path);

    this.#create(path, context.type);
  }

  dragStart(event) {
    const row = event.currentTarget;

    this.dragged = row.dataset.folder ?? row.dataset.path;
    this.dragRow = row;

    row.classList.add(...this.draggedClasses);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', this.dragged);
  }

  dragOver(event) {
    if (this.dragged === null) return;

    const folder = this.#dropFolder(event);

    event.dataTransfer.dropEffect = this.#movable(folder) ? 'move' : 'none';
    this.#markDropTarget(folder);
  }

  dragLeave(event) {
    if (this.asideTarget.contains(event.relatedTarget)) return;

    this.#clearDropTarget();
  }

  drop(event) {
    const dragged = this.dragged;
    const folder = this.#dropFolder(event);
    const destination = this.#movable(folder) ? this.#destination(folder) : null;

    this.dragEnd();

    if (destination === null) return;

    this.collapsed.delete(folder);
    this.#rename(dragged, destination);
  }

  dragEnd() {
    this.dragRow?.classList.remove(...this.draggedClasses);
    this.#clearDropTarget();

    this.dragged = null;
    this.dragRow = null;
  }

  focusNextEntry(event) {
    this.#moveFocus(event, 1);
  }

  focusPreviousEntry(event) {
    this.#moveFocus(event, -1);
  }

  expandEntry(event) {
    const entry = this.#entryFrom(event);

    if (entry === null || entry.dataset.folder === undefined) return;
    if (this.collapsed.has(entry.dataset.folder)) return this.#toggleFolder(entry.dataset.folder);

    this.#moveFocus(event, 1);
  }

  collapseEntry(event) {
    const entry = this.#entryFrom(event);

    if (entry === null) return;
    if (entry.dataset.folder !== undefined && !this.collapsed.has(entry.dataset.folder)) {
      return this.#toggleFolder(entry.dataset.folder);
    }

    this.#focusFolder(parentOf(entry.dataset.folder ?? entry.dataset.path));
  }

  activateEntry(event) {
    const entry = this.#entryFrom(event);

    if (entry === null) return;
    if (entry.dataset.folder !== undefined) return this.#toggleFolder(entry.dataset.folder);

    this.dispatch('open', { detail: { path: entry.dataset.path } });
  }

  #create(path, type) {
    this.collapsed.delete(path);
    this.#expandAncestors(path);
    this.dispatch('create', { detail: { path, type } });
  }

  #rename(from, to) {
    if (from === to) return this.#renderTree();

    this.collapsed = new Set([...this.collapsed].map((folder) => moved(folder, from, to)));

    this.#expandAncestors(to);
    this.dispatch('rename', { detail: { from, to } });
  }

  #toggleFolder(folder) {
    if (this.collapsed.has(folder)) {
      this.collapsed.delete(folder);
    } else {
      this.collapsed.add(folder);
    }

    this.#renderTree();
    this.#focusFolder(folder);
  }

  #expandAncestors(path) {
    for (const folder of ancestors(path)) this.collapsed.delete(folder);
  }

  #folderOf(event) {
    return event.currentTarget.closest('[data-folder]').dataset.folder;
  }

  #startRename(path, type) {
    this.#edit({ mode: 'rename', type, path, parent: parentOf(path) });
  }

  #edit(editing) {
    this.editing = { path: null, ...editing };

    this.dispatch('edit');
    this.collapsed.delete(editing.parent);
    this.#expandAncestors(editing.parent);
    this.#renderTree();

    const input = this.nameRow.querySelector('[data-role="input"]');

    input.focus();
    input.select();
  }

  #nameElement(depth) {
    const row = this.#row(this.nameTemplateTarget, depth);
    const input = row.querySelector('[data-role="input"]');

    row.querySelector('[data-role="file-icon"]').classList.toggle('hidden', this.editing.type !== 'file');
    row.querySelector('[data-role="folder-icon"]').classList.toggle('hidden', this.editing.type !== 'folder');

    input.placeholder = this.placeholdersValue[this.editing.type];
    input.value = this.editing.path === null ? '' : basename(this.editing.path);
    this.nameRow = row;

    return row;
  }

  #showNameError(message) {
    const input = this.nameRow.querySelector('[data-role="input"]');

    input.classList.toggle('text-danger', message !== null);
    input.classList.toggle('text-fg', message === null);
    input.setAttribute('aria-invalid', String(message !== null));
    input.title = message ?? '';
  }

  #nameError(value) {
    const name = this.#cleaned(value);

    if (name === '') return null;

    const segments = name.split('/');

    if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
      return this.nameErrorsValue.invalid.replace('%{name}', value.trim());
    }

    const path = this.editing.parent ? `${this.editing.parent}/${name}` : name;
    const renamed = this.editing.path;

    if (path === renamed) return null;
    if (renamed !== null && inside(path, renamed)) return this.nameErrorsValue.inside_itself;

    const blocking = this.#blockingFile(path);

    if (blocking !== null) return this.nameErrorsValue.not_a_folder.replace('%{name}', blocking);
    if (this.#taken(path)) return this.nameErrorsValue.taken.replace('%{name}', basename(path));

    return null;
  }

  #dropFolder(event) {
    const row = event.target.closest('[data-folder], [data-path]');

    if (row === null) return '';
    if (row.dataset.folder !== undefined) return row.dataset.folder;

    return parentOf(row.dataset.path);
  }

  #destination(folder) {
    const name = basename(this.dragged);

    return folder ? `${folder}/${name}` : name;
  }

  #movable(folder) {
    if (this.dragged === null) return false;
    if (inside(folder, this.dragged)) return false;
    if (parentOf(this.dragged) === folder) return false;

    return !this.#taken(this.#destination(folder));
  }

  #markDropTarget(folder) {
    const row = folder === '' ? this.rootTarget : this.#folderEntry(folder);

    if (row === this.dropRow) return;

    this.#clearDropTarget();

    this.dropRow = row ?? null;
    this.dropRow?.classList.add(...this.dropTargetClasses);
  }

  #clearDropTarget() {
    this.dropRow?.classList.remove(...this.dropTargetClasses);
    this.dropRow = null;
  }

  #folderEntry(folder) {
    return this.#entries().find((entry) => entry.dataset.folder === folder);
  }

  #taken(path) {
    return this.scene.files.some((file) => file.path === path) || this.#directories().has(path);
  }

  #blockingFile(path) {
    const found = ancestors(path).find((ancestor) => this.scene.files.some((file) => file.path === ancestor));

    return found ?? null;
  }

  #cleaned(value) {
    return value.trim().replace(/^\/+|\/+$/g, '');
  }

  #directories() {
    return directories(
      this.scene.files.map((file) => file.path),
      this.scene.emptyFolders
    );
  }

  #renderTree() {
    const hadFocus = this.treeTarget.contains(document.activeElement);
    const rows = this.#rowsIn('', 1).map((row) => this.#rowElement(row));

    this.treeTarget.replaceChildren(...rows);
    this.#renderTreeFocus(hadFocus);
  }

  #rowsIn(parent, depth) {
    const folders = this.#foldersIn(parent);
    const files = this.#filesIn(parent);
    const size = folders.length + files.length;
    const rows = [];

    if (this.#creatingIn(parent)) rows.push({ kind: 'name', depth });

    folders.forEach((folder, index) => {
      const collapsed = this.collapsed.has(folder);
      const kind = this.#renaming(folder) ? 'name' : 'directory';

      rows.push({ kind, path: folder, depth, size, collapsed, position: index + 1 });

      if (!collapsed) rows.push(...this.#rowsIn(folder, depth + 1));
    });

    files.forEach((file, index) => {
      const kind = this.#renaming(file.path) ? 'name' : 'file';

      rows.push({ kind, file, depth, size, position: folders.length + index + 1 });
    });

    return rows;
  }

  #creatingIn(parent) {
    return this.editing?.mode === 'create' && this.editing.parent === parent;
  }

  #renaming(path) {
    return this.editing?.mode === 'rename' && this.editing.path === path;
  }

  #rowElement(row) {
    if (row.kind === 'name') return this.#nameElement(row.depth);
    if (row.kind === 'directory') return this.#folderRow(row);

    return this.#fileRow(row);
  }

  #foldersIn(parent) {
    const folders = [...this.#directories()].filter((folder) => parentOf(folder) === parent);

    return folders.sort((one, other) => this.#compareNames(one, other));
  }

  #filesIn(parent) {
    const files = this.scene.files.filter((file) => parentOf(file.path) === parent);

    return files.sort((one, other) => this.#compareNames(one.path, other.path));
  }

  #compareNames(one, other) {
    return basename(one).localeCompare(basename(other), undefined, { numeric: true });
  }

  #renderTreeFocus(hadFocus) {
    const entries = this.#entries();
    const current = entries.find((entry) => entry.dataset.path === this.scene.activePath) ?? entries[0];

    if (current === undefined) return;

    for (const entry of entries) entry.tabIndex = entry === current ? 0 : -1;

    if (hadFocus && this.editing === null) current.focus();
  }

  #entries() {
    return [...this.treeTarget.querySelectorAll('[role="treeitem"]')];
  }

  #entryFrom(event) {
    if (this.editing !== null) return null;

    return event.target.closest('[role="treeitem"]');
  }

  #moveFocus(event, step) {
    const entry = this.#entryFrom(event);

    if (entry === null) return;

    const entries = this.#entries();
    const next = entries[entries.indexOf(entry) + step];

    if (next === undefined) return;

    this.#focusEntry(next);
  }

  #focusFolder(folder) {
    const entry = this.#folderEntry(folder);

    if (entry === undefined) return;

    this.#focusEntry(entry);
  }

  #focusEntry(entry) {
    for (const candidate of this.#entries()) candidate.tabIndex = candidate === entry ? 0 : -1;

    entry.focus();
  }

  #fileRow({ file, depth, position, size }) {
    const template = file.path.endsWith('.md') ? this.markdownTemplateTarget : this.fileTemplateTarget;
    const row = this.#row(template, depth);
    const active = file.path === this.scene.activePath;

    row.dataset.path = file.path;
    row.querySelector('[data-role="name"]').textContent = basename(file.path);
    row.querySelector('[data-role="dirty"]').hidden = !file.dirty;
    row.setAttribute('aria-selected', String(active));
    this.#markPosition(row, depth, position, size);
    paint(row, active, this.entryActiveClasses, this.entryIdleClasses);
    paint(row.querySelector(':scope > svg'), active, this.iconActiveClasses, this.iconIdleClasses);

    return row;
  }

  #folderRow({ path, depth, position, size, collapsed }) {
    const row = this.#row(this.folderTemplateTarget, depth);

    row.dataset.folder = path;
    row.querySelector('[data-role="name"]').textContent = basename(path);
    row.querySelector('[data-role="chevron"]').classList.toggle('rotate-90', !collapsed);
    row.setAttribute('aria-expanded', String(!collapsed));
    this.#markPosition(row, depth, position, size);

    return row;
  }

  #markPosition(row, depth, position, size) {
    row.setAttribute('aria-level', String(depth));
    row.setAttribute('aria-posinset', String(position));
    row.setAttribute('aria-setsize', String(size));
  }

  #row(template, depth) {
    const row = template.content.firstElementChild.cloneNode(true);

    row.style.paddingLeft = `${this.baseIndentValue + depth * this.indentStepValue}px`;

    return row;
  }
}
